import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { ArrowLeft, AlertTriangle, Clock, Users, Package, Download, FileText, Loader2, Truck, MapPin } from "lucide-react";
import { toast } from "sonner";
import EkinBrand from "@/components/EkinBrand";
import MultiPointMap from "@/components/MultiPointMap";

const STAGE_LABEL = { factura: "Factura", embarque: "Embarque", en_ruta: "En Ruta", entregado: "Entregado" };

function StatTile({ eyebrow, value, sub, accent }) {
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid={`stat-${eyebrow.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className={`eyebrow ${accent || "text-zinc-500"} mb-2`}>{eyebrow}</p>
      <p className="font-display text-4xl font-bold leading-none">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-2">{sub}</p>}
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function Metrics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  // Map date range filter
  const [mapFrom, setMapFrom] = useState("");
  const [mapTo, setMapTo] = useState("");
  const [mapPreset, setMapPreset] = useState("30d");

  const fetchMetrics = useCallback(async (from, to) => {
    try {
      const params = {};
      if (from) params.map_from = from;
      if (to) params.map_to = to;
      const res = await axios.get(`${API}/metrics`, { params });
      setData(res.data);
    } catch {
      toast.error("Error cargando métricas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Apply a preset (week / month / last_month / 30d) or custom
  const applyPreset = (preset) => {
    setMapPreset(preset);
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let from = "";
    let to = "";
    if (preset === "week") {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7; // monday=0
      d.setDate(d.getDate() - day);
      from = iso(d);
      to = iso(today);
    } else if (preset === "month") {
      from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
      to = iso(today);
    } else if (preset === "last_month") {
      from = iso(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      to = iso(new Date(today.getFullYear(), today.getMonth(), 0));
    } else {
      // 30d default
      from = "";
      to = "";
    }
    setMapFrom(from);
    setMapTo(to);
    fetchMetrics(from, to);
  };

  const applyCustomRange = () => {
    setMapPreset("custom");
    fetchMetrics(mapFrom, mapTo);
  };

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const res = await axios.get(`${API}/exports/completed.${kind}`, { responseType: "blob" });
      downloadBlob(res.data, `entregas-completadas.${kind}`);
      toast.success(`Exportado a ${kind.toUpperCase()}`);
    } catch {
      toast.error("Error al exportar");
    } finally {
      setExporting(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-900" />
      </div>
    );
  }

  const fmtHours = (h) => (h == null ? "—" : h < 1 ? `${Math.round(h * 60)} min` : `${h.toFixed(1)} h`);

  return (
    <div className="min-h-screen bg-zinc-50">
      <nav className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="p-2 hover:bg-zinc-100" data-testid="btn-back-dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <EkinBrand subtitle="Métricas" />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport("csv")}
              disabled={exporting === "csv"}
              data-testid="btn-export-csv"
              className="btn-tactile inline-flex items-center gap-2 px-3 py-2 text-xs border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50"
            >
              {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              CSV
            </button>
            <button
              onClick={() => handleExport("pdf")}
              disabled={exporting === "pdf"}
              data-testid="btn-export-pdf"
              className="btn-tactile inline-flex items-center gap-2 px-3 py-2 text-xs bg-ekin-600 text-white hover:bg-ekin-700 disabled:opacity-50"
            >
              {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              PDF
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
        <p className="eyebrow text-zinc-500 mb-2">Inteligencia operativa</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-8">
          Hola {user?.name?.split(" ")[0]}, así va la operación.
        </h1>

        {/* Top KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-8">
          <StatTile eyebrow="Total entregas" value={data.total} />
          <StatTile eyebrow="Completadas" value={data.completed} sub={`${data.total ? Math.round((data.completed / data.total) * 100) : 0}% del total`} />
          <StatTile eyebrow="Atrasadas" value={data.overdue_count} accent="text-rose-600" sub={data.overdue_count > 0 ? "Riesgo de incumplimiento" : "Sin atrasos"} />
          <StatTile eyebrow="Ciclo promedio" value={fmtHours(data.avg_cycle_hours)} sub="Captura → entrega" />
        </div>

        {/* Avg per stage */}
        <section className="bg-white border border-zinc-200 p-5 mb-8" data-testid="section-avg-stage">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-zinc-500" />
            <p className="eyebrow text-zinc-500">Tiempo promedio por etapa</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {["factura", "embarque", "en_ruta"].map((s) => (
              <div key={s} className="bg-white p-4">
                <div className={`h-1 stage-bar-${s} mb-3`} />
                <p className={`eyebrow stage-${s}`}>{STAGE_LABEL[s]}</p>
                <p className="font-display text-2xl font-bold mt-1">{fmtHours(data.avg_hours_by_stage?.[s])}</p>
                <p className="text-xs text-zinc-500 mt-1">tiempo en esta etapa</p>
              </div>
            ))}
          </div>
        </section>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Overdue */}
          <section className="bg-white border border-zinc-200" data-testid="section-overdue">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-600" />
                <p className="eyebrow text-zinc-500">Entregas atrasadas</p>
              </div>
              <span className="font-display font-bold text-rose-600">{data.overdue_count}</span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {data.overdue.length === 0 ? (
                <p className="p-5 text-sm text-zinc-500">Sin entregas atrasadas. Bien hecho.</p>
              ) : (
                <ul>
                  {data.overdue.map((o) => (
                    <li key={o.id} className="p-4 border-b border-zinc-100 last:border-0 flex items-center justify-between" data-testid={`overdue-${o.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-bold truncate">{o.cliente}</p>
                        <p className="text-xs text-zinc-500 truncate">{o.herramienta}</p>
                      </div>
                      <div className="text-right ml-3">
                        <p className={`eyebrow stage-${o.stage}`}>{STAGE_LABEL[o.stage]}</p>
                        <p className="text-xs text-rose-600 font-medium">{o.fecha_entrega}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* Top clients */}
          <section className="bg-white border border-zinc-200" data-testid="section-top-clients">
            <div className="p-5 border-b border-zinc-200 flex items-center gap-2">
              <Users className="h-4 w-4 text-zinc-500" />
              <p className="eyebrow text-zinc-500">Top clientes</p>
            </div>
            {data.top_clients.length === 0 ? (
              <p className="p-5 text-sm text-zinc-500">Sin datos.</p>
            ) : (
              <ul>
                {data.top_clients.map((c, i) => {
                  const max = data.top_clients[0]?.count || 1;
                  const pct = Math.round((c.count / max) * 100);
                  return (
                    <li key={c.cliente} className="p-4 border-b border-zinc-100 last:border-0" data-testid={`top-client-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-sm truncate">{c.cliente}</p>
                        <span className="font-display font-bold text-sm">{c.count}</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100">
                        <div className="h-full bg-ekin-600" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* Top drivers by avg route time + Map of recent deliveries */}
        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <section className="bg-white border border-zinc-200" data-testid="section-top-drivers">
            <div className="p-5 border-b border-zinc-200 flex items-center gap-2">
              <Truck className="h-4 w-4 text-zinc-500" />
              <p className="eyebrow text-zinc-500">Top choferes — tiempo promedio de ruta</p>
            </div>
            {!data.top_drivers || data.top_drivers.length === 0 ? (
              <p className="p-5 text-sm text-zinc-500">Sin datos de rutas todavía.</p>
            ) : (
              <ul>
                {data.top_drivers.map((dr, i) => {
                  const max = data.top_drivers[data.top_drivers.length - 1]?.avg_route_minutes || 1;
                  // Inverse bar (less time = more "good" — show shorter bars for faster)
                  const pct = Math.max(20, Math.min(100, (dr.avg_route_minutes / max) * 100));
                  const mins = dr.avg_route_minutes;
                  const label = mins < 60 ? `${Math.round(mins)} min` : `${(mins / 60).toFixed(1)} h`;
                  return (
                    <li key={dr.name} className="p-4 border-b border-zinc-100 last:border-0" data-testid={`top-driver-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-sm truncate">{dr.name}</p>
                        <span className="font-display font-bold text-sm" style={{ color: "#1A3A8C" }}>{label}</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100">
                        <div className="h-full bg-ekin-600" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-1.5 uppercase tracking-wider">
                        {dr.total_deliveries} entrega{dr.total_deliveries === 1 ? "" : "s"}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="bg-white border border-zinc-200" data-testid="section-map">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-zinc-500" />
                <p className="eyebrow text-zinc-500">Mapa de entregas</p>
              </div>
              <span className="text-xs text-zinc-500" data-testid="map-points-count">
                {data.recent_destinations?.length || 0} punto{data.recent_destinations?.length === 1 ? "" : "s"}
              </span>
            </div>

            {/* Filter chips */}
            <div className="px-5 pt-4 flex flex-wrap items-center gap-2" data-testid="map-filter-chips">
              {[
                { id: "30d", label: "Últimos 30 días" },
                { id: "week", label: "Esta semana" },
                { id: "month", label: "Este mes" },
                { id: "last_month", label: "Mes anterior" },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p.id)}
                  data-testid={`chip-${p.id}`}
                  className={`btn-tactile text-xs px-3 py-1.5 border transition-colors ${
                    mapPreset === p.id
                      ? "bg-ekin-600 text-white border-ekin-600"
                      : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Custom range */}
            <div className="px-5 pt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="map-custom-range">
              <span className="eyebrow text-zinc-500 mr-1">Rango</span>
              <input
                type="date"
                value={mapFrom}
                onChange={(e) => setMapFrom(e.target.value)}
                data-testid="map-input-from"
                className="border border-zinc-300 bg-white px-2 py-1.5"
              />
              <span className="text-zinc-400">—</span>
              <input
                type="date"
                value={mapTo}
                onChange={(e) => setMapTo(e.target.value)}
                data-testid="map-input-to"
                className="border border-zinc-300 bg-white px-2 py-1.5"
              />
              <button
                onClick={applyCustomRange}
                disabled={!mapFrom && !mapTo}
                data-testid="btn-apply-range"
                className="btn-tactile px-3 py-1.5 bg-ekin-600 text-white disabled:opacity-40"
              >
                Aplicar
              </button>
            </div>

            <div className="p-3 pt-3">
              <MultiPointMap points={data.recent_destinations || []} height={380} />
            </div>
          </section>
        </div>

        {/* Stage distribution footer */}
        <section className="mt-8 bg-white border border-zinc-200 p-5" data-testid="section-distribution">
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-4 w-4 text-zinc-500" />
            <p className="eyebrow text-zinc-500">Distribución por etapa</p>
          </div>
          <div className="grid grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {["factura", "embarque", "en_ruta", "entregado"].map((s) => (
              <div key={s} className="bg-white p-4">
                <div className={`h-1 stage-bar-${s} mb-3`} />
                <p className={`eyebrow stage-${s}`}>{STAGE_LABEL[s]}</p>
                <p className="font-display text-3xl font-bold mt-1">{data.by_stage?.[s] || 0}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
